// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';
import { Cell, Row } from 'react-table';

interface TableRowProps<D extends object> {
    /**
     * `index` parameter provided by react-window's render props
     */
    index: number;
    /**
     * The react-table row instance
     */
    row: Row<D>;
    /**
     * `style` parameter provided by react-window's render props
     */
    style: Record<string, unknown>;
    /**
     * Tailwind size to apply to text
     */
    textSize?: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders a table row. Intended for use within a render function for <FixedSizeList> from
 * react-window - see docs: {@link https://react-window.vercel.app/#/examples/list/fixed-size}
 * @param props - {@link TableRowProps}
 */
export function TableRow<D extends object>({
    index,
    row,
    style,
    textSize = 'xs',
}: TableRowProps<D>): JSX.Element {
    const ariaRowIndex = index + 1;
    const evenRow = ariaRowIndex % 2 === 0;
    const rowProps = row.getRowProps({
        style,
    });
    const classNames = cx('tr relative flex items-center', {
        'even-row': evenRow,
    });

    return (
        <div {...rowProps} aria-rowindex={ariaRowIndex} className={classNames}>
            {row.cells.map((cell: Cell<D>) => {
                const { key, ...rest } = cell.getCellProps();

                return (
                    <div key={key} {...rest} className={`td text-${textSize}`}>
                        {cell.render('Cell')}
                    </div>
                );
            })}
        </div>
    );
}
